/**
 * NetDocuments Config Manager
 *
 * Manages cachedNdConfig (from URL params) and defaultNdConfig (from API)
 * in localStorage. Used by the ND iframe integration flow.
 */

import { apiClient } from '@/api/client';
import { getValidNdAccessToken } from '@/utils/ndTokenManager';

// ── Storage keys ────────────────────────────────────────────────────────────
const CACHED_ND_CONFIG_KEY = 'cachedNdConfig';
const DEFAULT_ND_CONFIG_KEY = 'defaultNdConfig';

// ── Types ───────────────────────────────────────────────────────────────────

/** Parameters parsed from the ND iframe URL */
export interface NdCachedConfig {
  /** Portfolio key (e.g., "ACQ1") */
  pk?: string;
  /** Cabinet key (e.g., "1000") */
  ck?: string;
  /** Portfolio detail / description (e.g., "Acquisition Review") */
  pd?: string;
  /** Cabinet detail / description (e.g., "Thousand Miles Corp.") */
  cd?: string;
  /** Username (e.g., "Mike Tobias") */
  un?: string;
  /** Current ND page URL */
  curpage?: string;
  /** Source indicator */
  from?: string;
  /** Mode indicator */
  mode?: string;
  [key: string]: string | undefined;
}

// ── Read/Write helpers ──────────────────────────────────────────────────────

export const getCachedNdConfig = (): NdCachedConfig | null => {
  try {
    const raw = localStorage.getItem(CACHED_ND_CONFIG_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as NdCachedConfig;
  } catch {
    return null;
  }
};

export const setCachedNdConfig = (config: NdCachedConfig): void => {
  localStorage.setItem(CACHED_ND_CONFIG_KEY, JSON.stringify(config));
};

export const clearCachedNdConfig = (): void => {
  localStorage.removeItem(CACHED_ND_CONFIG_KEY);
};

export const getDefaultNdConfig = <T = unknown>(): T | null => {
  try {
    const raw = localStorage.getItem(DEFAULT_ND_CONFIG_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

/** Custom event name used to notify same-tab listeners when defaultNdConfig changes */
const ND_DEFAULT_CONFIG_CHANGED = 'nd-default-config-changed';

export const setDefaultNdConfig = (config: unknown): void => {
  localStorage.setItem(DEFAULT_ND_CONFIG_KEY, JSON.stringify(config));
  window.dispatchEvent(new Event(ND_DEFAULT_CONFIG_CHANGED));
};

export const clearDefaultNdConfig = (): void => {
  localStorage.removeItem(DEFAULT_ND_CONFIG_KEY);
  window.dispatchEvent(new Event(ND_DEFAULT_CONFIG_CHANGED));
};

// ── WIP filter cleanup ──────────────────────────────────────────────────────

/**
 * Clear all WIP filter entries from localStorage.
 * Mirrors Angular's `clearWipFiltersForNDFrame()`.
 */
export const clearWipFiltersForNDFrame = (): void => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.includes('wipFilters')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

// ── URL param parsing ───────────────────────────────────────────────────────

/**
 * Parse ND query parameters from the current URL.
 * Returns null if `from=nd` is not present.
 *
 * Mirrors Angular's home.component.ts URL parsing logic.
 */
export const parseNdParamsFromUrl = (): NdCachedConfig | null => {
  const decodedUrl = decodeURIComponent(window.location.href);

  if (!decodedUrl.includes('from=nd')) {
    return null;
  }

  // Parse all query params from the URL
  const queryString = decodedUrl.includes('?')
    ? decodedUrl.split('?')[1]
    : '';

  if (!queryString) return null;

  const params: NdCachedConfig = {};
  queryString.split('&').forEach(pair => {
    const [key, ...valueParts] = pair.split('=');
    if (key) {
      params[decodeURIComponent(key)] = decodeURIComponent(valueParts.join('='));
    }
  });

  return params;
};

// ── API call ────────────────────────────────────────────────────────────────

/**
 * Call /NDApi/GetDefaultClientDetails with cached ND config params.
 * Mirrors Angular's ndapi.service.ts `getDefaultClientDetails()`.
 *
 * Returns the API response data, or null on failure.
 */
export const fetchDefaultClientDetails = async (
  cachedConfig: NdCachedConfig,
): Promise<Record<string, string> | null> => {
  try {
    const ndAccessToken = await getValidNdAccessToken();

    // Use apiClient which adds Bearer token + tenant header automatically
    const response = await apiClient.get(
      '/NDApi/GetDefaultClientDetails',
      {
        params: {
          pk: cachedConfig.pk || '',
          ck: cachedConfig.ck || '',
          pd: cachedConfig.pd || '',
          cd: cachedConfig.cd || '',
          un: cachedConfig.un || '',
          ND_CurrentPage: cachedConfig.curpage || '',
        },
        headers: {
          ...(ndAccessToken ? { nd_access_token: ndAccessToken } : {}),
        },
      },
    );

    // The backend returns Content(JsonConvert.SerializeObject(...)) which is
    // text/plain, so axios may return a raw JSON string instead of a parsed object.
    let data = response.data;
    if (typeof data === 'string') {
      if (!data || data.trim() === '') return null;
      try {
        data = JSON.parse(data);
      } catch {
        console.error('Failed to parse GetDefaultClientDetails response:', data);
        return null;
      }
    }

    // Validate that we got the expected shape
    if (!data || typeof data !== 'object') return null;

    return data as Record<string, string>;
  } catch (err) {
    console.error('Failed to fetch default client details:', err);
    return null;
  }
};
